import baseAPI, { BookingCalendarEventDetailInternalResponse, CalendarEventPlatformType } from 'api';
import useApplicationSettings from 'hooks/useApplicationSettings';
import React, { useEffect } from 'react';
import { BookingFormState, STEP } from './types';
import { Updater, useImmer } from 'use-immer';
import { useTranslation } from 'react-i18next';
import Loading from 'components/Loading';

type VideoPlatform = {
  label: string;
  value: CalendarEventPlatformType;
  enabled: boolean;
};

type ContextProps = {
  advisorId: string;
  advisorName: string;
  availableSlots: BookingCalendarEventDetailInternalResponse[];
  state: BookingFormState;
  videoPlatforms: VideoPlatform[];
  clearUserInfo: VoidFunction;
  setStep: (newStep: STEP) => void;
  setState: Updater<BookingFormState>;
};

type Props = { advisorId: string; advisorName: string; availableSlots: BookingCalendarEventDetailInternalResponse[] };

export const BookingFormContext = React.createContext<ContextProps>({} as any);

export const BookingFormProvider = ({
  advisorId,
  advisorName,
  availableSlots,
  children,
}: React.PropsWithChildren<Props>) => {
  // Check user here
  const { t } = useTranslation();
  const { appSettings } = useApplicationSettings();
  const { isBookingFormWisoryVideoEnabled, isWisoryVideoNextEnabled } = appSettings;
  const WISORY_VIDEO_PLATFORM_VALUE = isWisoryVideoNextEnabled
    ? CalendarEventPlatformType.WISORY_VIDEO_NEXT
    : CalendarEventPlatformType.WISORY_VIDEO;
  const [isReady, setIsReady] = useImmer(false);
  const [state, setState] = useImmer<BookingFormState>({
    workflow: availableSlots.length === 0 ? 'suggest-timespans' : 'select-time',
    step: 'select-time-or-suggest-timespans',
    previousStep: 'select-time-or-suggest-timespans',
    selectedSlot: undefined,
    suggestedSlots: [
      { startDate: undefined, endDate: undefined },
      { startDate: undefined, endDate: undefined },
      { startDate: undefined, endDate: undefined },
    ],
    customerInfo: {
      isWisoryUser: false,
      customerName: '',
      customerPhoneNumber: '',
      customerEmail: '',
      customerCompany: '',
      customerBillingAddress: '',
      reference: '',
      goal: '',
      purpose: '',
      videoPlatform: isBookingFormWisoryVideoEnabled ? WISORY_VIDEO_PLATFORM_VALUE : CalendarEventPlatformType.MS_TEAMS,
    },
  });
  const videoPlatforms = [
    {
      label: t('Wisory Video'),
      value: WISORY_VIDEO_PLATFORM_VALUE,
      enabled: isBookingFormWisoryVideoEnabled || false,
    },
    {
      label: t('Microsoft Teams'),
      value: CalendarEventPlatformType.MS_TEAMS,
      enabled: true,
    }
  ];

  const clearUserInfo = () => {
    setState((draft) => {
      draft.customerInfo.customerCompany = '';
      draft.customerInfo.customerEmail = '';
      draft.customerInfo.customerName = '';
      draft.customerInfo.customerPhoneNumber = '';
      draft.customerInfo.customerBillingAddress = '';
      draft.customerInfo.isWisoryUser = false;
    });
  };

  const setStep = (newStep: STEP) => {
    setState((draft) => {
      const currentStep = draft.step;
      const isLoggedIn = state.customerInfo.isWisoryUser;
      // If go back from confirm step after login successfully
      if (newStep === 'login-form' && currentStep === 'confirm-booking' && isLoggedIn) {
        // Skip Login or Guest Screen
        draft.previousStep = 'confirm-booking';
        draft.step = 'enter-goal-and-purpose';
        return;
      }

      if (newStep === 'login-or-enter-as-guest' && currentStep === 'enter-goal-and-purpose' && isLoggedIn) {
        // Skip Login or Guest Screen
        draft.step = 'confirm-booking';
        draft.previousStep = 'enter-goal-and-purpose';
        return;
      }

      draft.previousStep = draft.step;
      draft.step = newStep;
    });
  };

  useEffect(() => {
    async function getLoggedUserInfo() {
      setIsReady(false);

      try {
        const { data } = await baseAPI.api.getMyAccount();

        setState((draft) => {
          draft.customerInfo.customerCompany = data.company?.companyName || '';
          draft.customerInfo.customerEmail = data.email || '';
          draft.customerInfo.customerName = data.fullName || '';
          draft.customerInfo.customerPhoneNumber = data.phoneNumber || '';
          draft.customerInfo.isWisoryUser = true;
        });
      } finally {
        setIsReady(true);
      }
    }

    getLoggedUserInfo();
  }, [setIsReady, setState]);

  if (!isReady) {
    <div className="flex items-center justify-center h-72">
      <Loading />
    </div>;
  }

  return (
    <BookingFormContext.Provider
      value={{
        advisorId,
        advisorName,
        availableSlots,
        state,
        videoPlatforms,
        clearUserInfo,
        setStep,
        setState,
      }}
    >
      {children}
    </BookingFormContext.Provider>
  );
};
